import { create, fromBinary, toBinary } from '@bufbuild/protobuf';
import {
  ClientFrameSchema,
  type ClientMessage,
} from '../protocol/gen/warinspace/v2/client_to_server_pb.js';
import {
  ServerFrameSchema,
  type ServerMessage,
} from '../protocol/gen/warinspace/v2/server_to_client_pb.js';
import { PING_INTERVAL_MS, RECONNECT_DELAY_MS } from '../game/constants/feel';
import { isAuthenticatedSubprotocol, offeredSubprotocols } from './session-subprotocol';

export interface ConnectionOptions {
  readonly url: string;
  readonly onPacket: (message: ServerMessage) => void;
  readonly onStateChange?: ((state: ConnectionState) => void) | undefined;
  readonly onDecodeError?: ((error: Error, unreadBytes: number) => void) | undefined;
  readonly autoReconnect?: boolean | undefined;
  /** Read at connect time, not captured at construction, since a token acquired after this object is built would otherwise be stale. */
  readonly sessionToken?: (() => string | null) | undefined;
  readonly onAuthenticated?: ((authenticated: boolean) => void) | undefined;
}

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed';

export class Connection {
  private socket: WebSocket | null = null;
  private state: ConnectionState = 'idle';
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private restartNoted = false;

  private pingSentAt: number | null = null;
  private outstandingPing: bigint | null = null;
  private pingNonce = 0n;
  private sequence = 0n;
  private authenticated = false;

  roundTripMs = 0;

  constructor(private readonly options: ConnectionOptions) {}

  get currentState(): ConnectionState {
    return this.state;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  get isAuthenticated(): boolean {
    return this.isOpen && this.authenticated;
  }

  get isRestartPending(): boolean {
    return this.restartNoted;
  }

  /** The next close reloads the page instead of reconnecting, so the player picks up the redeployed build. */
  noteServerRestarting(): void {
    this.restartNoted = true;
  }

  connect(): void {
    if (this.disposed || this.state === 'connecting' || this.state === 'open') return;
    this.clearReconnect();
    this.setState('connecting');
    this.authenticated = false;

    let socket: WebSocket;
    try {
      socket = new WebSocket(
        this.options.url,
        offeredSubprotocols(this.options.sessionToken?.() ?? null),
      );
    } catch (cause) {
      this.options.onDecodeError?.(
        cause instanceof Error ? cause : new Error('WebSocket construction failed'),
        0,
      );
      this.handleClose();
      return;
    }

    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = () => {
      this.authenticated = isAuthenticatedSubprotocol(socket.protocol);
      this.setState('open');
      this.options.onAuthenticated?.(this.authenticated);
      this.startPinging();
    };

    socket.onmessage = (event: MessageEvent<unknown>) => {
      this.handleFrame(event.data);
    };

    socket.onerror = () => {};

    socket.onclose = () => {
      this.handleClose();
    };
  }

  send(message: ClientMessage): void {
    if (!this.isOpen || this.socket === null) return;
    this.sequence += 1n;
    const frame = create(ClientFrameSchema, { sequence: this.sequence, messages: [message] });
    this.socket.send(toBinary(ClientFrameSchema, frame));
  }

  ping(): void {
    if (!this.isOpen) return;
    this.pingNonce += 1n;
    this.pingSentAt = performance.now();
    this.outstandingPing = this.pingNonce;
    this.send({
      $typeName: 'warinspace.v2.ClientMessage',
      payload: {
        case: 'ping',
        value: { $typeName: 'warinspace.v2.Ping', clientNonce: this.pingNonce },
      },
    });
  }

  /** A connection cannot become authenticated in place — the token is presented on the handshake only — so logging in costs a reconnect. */
  reconnect(): void {
    if (this.disposed) return;
    this.stopPinging();
    this.clearReconnect();
    /** A manual reconnect starts a fresh connection lifecycle; a restart noted on the old socket must not reload the new one. */
    this.restartNoted = false;

    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
    this.authenticated = false;
    this.setState('idle');
    this.connect();
  }

  dispose(): void {
    this.disposed = true;
    this.stopPinging();
    this.clearReconnect();
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
    this.setState('closed');
  }

  private handleFrame(data: unknown): void {
    if (!(data instanceof ArrayBuffer)) {
      this.options.onDecodeError?.(new Error(`Expected a binary frame, got ${typeof data}`), 0);
      return;
    }

    let frame;
    try {
      frame = fromBinary(ServerFrameSchema, new Uint8Array(data));
    } catch (cause) {
      this.options.onDecodeError?.(
        cause instanceof Error ? cause : new Error('Malformed server frame'),
        data.byteLength,
      );
      return;
    }

    for (const message of frame.messages) {
      if (message.payload.case === 'pong') {
        this.absorbPong(message.payload.value.clientNonce);
        continue;
      }
      this.options.onPacket(message);
    }
  }

  /** A pong whose nonce isn't the outstanding ping's is from before a reconnect and must not be folded in — it would time the gap between sockets. */
  private absorbPong(nonce: bigint): void {
    if (this.pingSentAt === null || nonce !== this.outstandingPing) return;
    const sample = performance.now() - this.pingSentAt;
    this.pingSentAt = null;
    this.outstandingPing = null;
    this.roundTripMs = this.roundTripMs === 0 ? sample : (this.roundTripMs * 3 + sample) / 4;
  }

  private startPinging(): void {
    this.stopPinging();
    this.ping();
    this.pingTimer = setInterval(() => {
      this.ping();
    }, PING_INTERVAL_MS);
  }

  private stopPinging(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.pingSentAt = null;
    this.outstandingPing = null;
  }

  private handleClose(): void {
    this.stopPinging();
    this.socket = null;
    this.authenticated = false;
    this.setState('closed');
    if (this.disposed) return;

    /** The caller (main.ts) owns reload timing — it alone knows whether an end-game result is still on screen. */
    if (this.restartNoted) return;

    if (this.options.autoReconnect ?? true) {
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, RECONNECT_DELAY_MS);
    }
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.options.onStateChange?.(state);
  }
}
